import React, { useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import {
  MyStoreProduct,
  StoreProductInput,
  StoreProductUploadResult,
  StoreProductUploadStatus,
  storeProductUploadApi,
} from '../../services/storeProductUploadApi';
import { tokens } from '../../theme/tokens';
import { InlineMessage, LoadingState, SectionCard, StatusChip } from '../ui';
import type { StatusTone } from '../ui';

interface StoreProductUploadCardProps {
  /** Ids already in the selection the page shows, so an uploaded product reads In sync selection at once. */
  selectedIds: Set<string>;
}

const STATUS_TONE: Record<StoreProductUploadStatus, StatusTone> = {
  matched: 'info',
  created: 'positive',
  updated: 'info',
  rejected: 'problem',
};

const emptyForm = (): Record<keyof StoreProductInput, string> => ({
  brand: '',
  model: '',
  name: '',
  upc: '',
  sku: '',
  msrp: '',
  map: '',
  category: '',
  description: '',
  imageUrls: '',
});

const FORM_FIELDS: Array<{ key: keyof StoreProductInput; multiline?: boolean; required?: boolean }> = [
  { key: 'brand', required: true },
  { key: 'model', required: true },
  { key: 'name' },
  { key: 'upc' },
  { key: 'sku' },
  { key: 'msrp' },
  { key: 'map' },
  { key: 'category' },
  { key: 'description', multiline: true },
  { key: 'imageUrls', multiline: true },
];

/**
 * The product browser's "Add products" section (Jordan, store shell, Products).
 * A store adds products Motzu does not carry, by CSV or a form. They are
 * matched first (barcode, brand + SKU, model): a match links the store's SKU,
 * anything else becomes a product visible to this store only, which it can add
 * to its sync selection and which merges into the real product if Motzu ever
 * carries it.
 */
const StoreProductUploadCard: React.FC<StoreProductUploadCardProps> = ({ selectedIds }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [result, setResult] = useState<StoreProductUploadResult | null>(null);

  const { data: mine, isLoading } = useQuery({
    queryKey: ['store-uploaded-products'],
    queryFn: () => storeProductUploadApi.listMine(),
  });

  const onResult = (data: StoreProductUploadResult) => {
    setResult(data);
    queryClient.invalidateQueries({ queryKey: ['store-uploaded-products'] });
  };
  const onError = (error: unknown) => showToast(error instanceof Error ? error.message : t('store.productUpload.error'), 'error');

  const uploadCsv = useMutation({
    mutationFn: (file: File) => storeProductUploadApi.uploadCsv(file),
    onSuccess: onResult,
    onError,
  });

  const createOne = useMutation({
    mutationFn: (input: StoreProductInput) => storeProductUploadApi.createOne(input),
    onSuccess: (data) => {
      onResult(data);
      if (data.rejected === 0) setForm(emptyForm());
    },
    onError,
  });

  const addToSync = useMutation({
    mutationFn: (id: string) => storeProductUploadApi.select([id]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      showToast(t('store.productUpload.added'), 'success');
    },
    onError,
  });

  const submitForm = () => {
    const { imageUrls, ...rest } = form;
    createOne.mutate({ ...rest, imageUrls: imageUrls.split(/\s+/).filter(Boolean) });
  };

  const downloadTemplate = async () => {
    try {
      await storeProductUploadApi.downloadTemplate();
    } catch (error) {
      onError(error);
    }
  };

  const busy = uploadCsv.isPending || createOne.isPending;
  const products: MyStoreProduct[] = mine || [];

  return (
    <SectionCard title={t('store.productUpload.title')} subtitle={t('store.productUpload.subtitle')} dense padded>
      <InlineMessage severity="info">{t('store.productUpload.privacy')}</InlineMessage>

      <Stack direction="row" spacing={1} useFlexGap sx={{ my: 2, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={downloadTemplate}>{t('store.productUpload.template')}</Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={() => fileInput.current?.click()}>
          {t('store.productUpload.uploadCsv')}
        </Button>
        <Button size="small" variant={showForm ? 'contained' : 'outlined'} aria-pressed={showForm} onClick={() => setShowForm((prev) => !prev)}>
          {t('store.productUpload.addOne')}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          hidden
          data-testid="store-product-csv"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) uploadCsv.mutate(file);
          }}
        />
      </Stack>

      {showForm && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, mb: 2 }}>
          {FORM_FIELDS.map(({ key, multiline, required }) => (
            <TextField
              key={key}
              size="small"
              label={t(`store.productUpload.field.${key}`)}
              helperText={key === 'imageUrls' ? t('store.productUpload.imageHelp') : undefined}
              required={required}
              multiline={multiline}
              minRows={multiline ? 2 : undefined}
              value={form[key]}
              onChange={(event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))}
              sx={multiline ? { gridColumn: { sm: '1 / -1' } } : undefined}
            />
          ))}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Button variant="contained" size="small" disabled={busy || !form.brand.trim() || !form.model.trim()} onClick={submitForm}>
              {t('store.productUpload.submit')}
            </Button>
          </Box>
        </Box>
      )}

      {busy && <LoadingState />}

      {result && (
        <Box sx={{ mb: 2 }} data-testid="store-product-result">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('store.productUpload.summary', { matched: result.matched, created: result.created, updated: result.updated, rejected: result.rejected })}
          </Typography>
          {result.pricing.status === 'applied' && (
            <InlineMessage severity="success">{t('store.productUpload.pricingApplied', { count: result.pricing.rows })}</InlineMessage>
          )}
          {result.pricing.status === 'failed' && (
            <InlineMessage severity="warning">
              {t('store.productUpload.pricingFailed', { message: result.pricing.message || '' })}
            </InlineMessage>
          )}
          {result.rows.map((row) => (
            <Box
              key={row.row}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, flexWrap: 'wrap' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
                {t('store.productUpload.row', { row: row.row })}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography variant="body2">{[row.brand, row.model].filter(Boolean).join(' ') || '-'}</Typography>
                {(row.reason || row.warnings.length > 0 || row.matchedBy) && (
                  <Typography variant="caption" color="text.secondary">
                    {[row.reason, row.matchedBy ? t(`store.productUpload.matchedBy.${row.matchedBy}`) : null, ...row.warnings]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                )}
              </Box>
              <StatusChip label={t(`store.productUpload.status.${row.status}`)} tone={STATUS_TONE[row.status]} size="small" />
            </Box>
          ))}
        </Box>
      )}

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('store.productUpload.mineTitle')}</Typography>
      {isLoading && <LoadingState />}
      {!isLoading && products.length === 0 && (
        <Typography variant="caption" color="text.secondary">{t('store.productUpload.mineEmpty')}</Typography>
      )}
      {products.map((product) => {
        const selected = selectedIds.has(product.id);
        return (
          <Box
            key={product.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, flexWrap: 'wrap' }}
          >
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{product.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[product.brand, product.model, product.sku ? `SKU ${product.sku}` : null, product.upc ? `UPC ${product.upc}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
            <StatusChip label={t('store.productUpload.private')} tone="neutral" size="small" />
            {selected ? (
              <StatusChip label={t('store.productUpload.selected')} tone="positive" size="small" />
            ) : (
              <Button size="small" variant="outlined" disabled={addToSync.isPending} onClick={() => addToSync.mutate(product.id)}>
                {t('store.productUpload.addToSync')}
              </Button>
            )}
          </Box>
        );
      })}
    </SectionCard>
  );
};

/** The chip on the picker's filter row that opens the section. */
export const StoreProductUploadToggle: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const { t } = useAppTranslation();
  return (
    <Button
      size="small"
      variant={open ? 'contained' : 'outlined'}
      onClick={onToggle}
      aria-pressed={open}
      sx={{ height: 38, whiteSpace: 'nowrap' }}
    >
      {t('store.productUpload.title')}
    </Button>
  );
};

export default StoreProductUploadCard;
